import { FC } from "react";

import { MappedStudentAssessmentScoreTableRow } from "./utils";

import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { CommentIcon, EyeNotVisibleIcon } from "mds/icons";
import { useSectionCapabilitiesBySectionId } from "store/capability-hooks";
import { Link } from "utils/routing";
import { pageUrl } from "utils/urls";

type StudentScoreOverviewTableProps = {
  sectionId: string;
  tableRows: MappedStudentAssessmentScoreTableRow[];
};

export const StudentScoreOverviewTable: FC<StudentScoreOverviewTableProps> = ({
  sectionId,
  tableRows,
}) => {
  const { canParticipateInSection } = useSectionCapabilitiesBySectionId(sectionId);

  if (tableRows.length === 0) {
    return null;
  }

  return (
    <ScrollableTableContainer
      className="w-full overflow-hidden"
      title={t("assessment_dashboard.all.title")}
    >
      <Table>
        {/* Headers: Page / Page Type / Points / Outcomes / Submitted At */}
        <THead>
          <TR className="border-black-tint-70">
            <TH className="pl-4">{t("assessment_dashboard.all.page_topic_header_title")}</TH>

            <TH>{t("assessment_dashboard.all.page_header_title")}</TH>

            <TH>
              <TooltipTrigger
                content={t("assessment_dashboard.all.assessment_category_header_tooltip")}
              >
                {t("assessment_dashboard.all.assessment_category_header_title")}
              </TooltipTrigger>
            </TH>

            <TH>{t("assessment_dashboard.student.points_header_title")}</TH>

            <TH>{t("assessment_dashboard.all.outcomes_header_title")}</TH>

            <TH>{t("assessment_dashboard.student.submitted_header_title")}</TH>
          </TR>
        </THead>

        <tbody>
          {tableRows.map((row) => (
            <TR key={row.id}>
              <TD className="max-w-52 truncate py-2.5 pl-[17px]" title={row.topicTitle || "–"}>
                {row.topicTitle || "–"}
              </TD>

              <TD className="w-52">
                <div className="flex max-w-52 items-center gap-2">
                  <div className="body-xs flex min-w-0 items-center gap-1">
                    {row.hidden && (
                      <TooltipTrigger content={t("assessment_dashboard.all.hidden_page_tooltip")}>
                        <EyeNotVisibleIcon className="inline shrink-0 text-black-tint-40" />
                      </TooltipTrigger>
                    )}
                    {row.hidden && canParticipateInSection ? (
                      <span className="truncate">{row.title}</span>
                    ) : (
                      <Link
                        className="min-w-0 truncate text-blue-shade-20 underline"
                        to={pageUrl(sectionId, row.id)}
                      >
                        {row.title}
                      </Link>
                    )}
                  </div>

                  {row.assessmentComments.length > 0 && (
                    <div className="flex shrink-0 items-center gap-1">
                      {row.assessmentComments.map((assessmentComment) => (
                        <TooltipTrigger
                          content={assessmentComment.comment}
                          key={assessmentComment.assessment_id}
                          label={t("assessment.instructor_comment")}
                          positionStrategy="fixed"
                        >
                          <CommentIcon className="shrink-0 text-blue-shade-40" size="l" />
                        </TooltipTrigger>
                      ))}
                    </div>
                  )}
                </div>
              </TD>

              <TD>{row.assessmentCategory}</TD>

              <TD>{row.points}</TD>

              <TD>
                <TooltipTrigger content={row.outcomesTooltip}>
                  {row.outcomesCount.toString()}
                </TooltipTrigger>
              </TD>

              <TD>{row.submittedAt}</TD>
            </TR>
          ))}
        </tbody>
      </Table>
    </ScrollableTableContainer>
  );
};
